import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  IcSearch,
  IcClose,
  IcPlus,
  IcPackageOpen,
  IcChevronRight,
  IcFilters,
  IcPercent,
} from '../../theme/icons';
import { useAppearance } from '../../context/AppearanceContext';
import productService from '../../services/productService';
import authService from '../../services/authService';
import categoryService from '../../services/categoryService';
import subcategoryService from '../../services/subcategoryService';
import laboratoireService from '../../services/laboratoireService';
import ProductDetailModal from '../../pages/products/ProductDetailModal';

const STOCK_STATES = [
  { value: 'in', label: 'En stock' },
  { value: 'low', label: 'Stock bas' },
  { value: 'out', label: 'Rupture' },
];

function StockPill({ product }) {
  const stock = typeof product.stock === 'number' ? product.stock : 0;
  const isOut = stock <= 0;
  const isLow = !isOut && stock <= product.stock_min;
  // Variants = grammaire Étiquettes Ki Para (index.css) — famille stock.
  const cls = isOut
    ? 'bdg-critical'
    : isLow
    ? 'bdg-stock-low'
    : 'bdg-stock-ok';
  const label = isOut ? 'Rupture' : `${stock} en stock`;
  return (
    <span className={`bdg ${cls}`}>
      {label}
    </span>
  );
}

function ProductCard({ product, onOpen }) {
  const initials = (product.name || '?')
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <button
      type="button"
      onClick={() => onOpen(product)}
      className="w-full text-left flex items-center gap-3 p-3 bg-white rounded-2xl border border-gray-200 active:bg-gray-50 transition-colors"
    >
      <div className="h-14 w-14 rounded-xl bg-gray-100 text-gray-400 flex-shrink-0 flex items-center justify-center overflow-hidden font-semibold">
        {product.image ? (
          <img src={product.image} alt={product.name} className="h-full w-full object-cover" />
        ) : (
          initials
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="font-medium text-gray-900 truncate">{product.name}</div>
        <div className="text-xs text-gray-500 truncate">
          {product.category?.name || 'Sans catégorie'}
        </div>
        <div className="mt-1 flex items-center gap-2">
          <StockPill product={product} />
          {product.price ? (
            <span className="text-sm font-semibold text-gray-800">
              {Number(product.price).toFixed(3)}
              <span className="text-[11px] text-gray-500 ml-0.5">DT</span>
            </span>
          ) : null}
        </div>
      </div>

      <IcChevronRight className="text-gray-300 flex-shrink-0" />
    </button>
  );
}

export default function MobileProductList() {
  const navigate = useNavigate();
  const { text: appText } = useAppearance();
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [detailProduct, setDetailProduct] = useState(null);
  const canManage = authService.isManagerOrAdmin();

  // Filtres — mêmes critères que la page Produits web (panneau repliable).
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubcategory, setSelectedSubcategory] = useState('');
  const [selectedLaboratoire, setSelectedLaboratoire] = useState('');
  const [selectedStockState, setSelectedStockState] = useState('');
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [laboratoires, setLaboratoires] = useState([]);

  useEffect(() => {
    categoryService.getAllCategories()
      .then((d) => setCategories(Array.isArray(d) ? d : d?.results || []))
      .catch((err) => console.error('Error fetching categories:', err));
    subcategoryService.getAllSubcategories()
      .then((d) => setSubcategories(Array.isArray(d) ? d : d?.results || []))
      .catch((err) => console.error('Error fetching subcategories:', err));
    laboratoireService.getAllLaboratoires()
      .then((d) => setLaboratoires(Array.isArray(d) ? d : d?.results || []))
      .catch((err) => console.error('Error fetching laboratoires:', err));
  }, []);

  const filteredSubcategories = useMemo(
    () =>
      selectedCategory
        ? subcategories.filter((sub) => {
            const categoryId = typeof sub.category === 'object' ? sub.category.id : sub.category;
            return categoryId && categoryId.toString() === selectedCategory;
          })
        : [],
    [subcategories, selectedCategory]
  );

  const load = useCallback(async (p, s, append) => {
    setLoading(true);
    try {
      const { results, count: total } = await productService.getProductsPage({
        page: p,
        pageSize: 30,
        search: s,
        category: selectedCategory,
        subcategory: selectedSubcategory,
        laboratoire: selectedLaboratoire,
        stockState: selectedStockState,
      });
      setCount(total);
      setProducts((prev) => (append ? [...prev, ...results] : results));
    } catch (err) {
      console.error('Error loading products:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, selectedSubcategory, selectedLaboratoire, selectedStockState]);

  // Debounced search — resets to page 1 whenever the term changes.
  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      load(1, search, false);
    }, 350);
    return () => clearTimeout(t);
  }, [search, load]);

  const loadMore = () => {
    const next = page + 1;
    setPage(next);
    load(next, search, true);
  };

  const handleEdit = (id) => navigate(`/products/${id}/edit`);

  return (
    /* Owner 2026-09-09 : même enveloppe que « Nouvelle vente » (/seller-sales)
       — `px-4 pt-2 pb-4`, la page commence à la même hauteur. */
    <div className="w-full px-4 pt-2 pb-4">
      {/* Owner 2026-09-09 : ligne d'en-tête au design de « Nouvelle vente » —
          à gauche le chip de page (blanc, bordure noire, ombre, icône 13px)
          comme le bouton « Nouvelle vente » de VenteTabs ; à droite le kit
          « pilule » .segp avec « Promo » (même look que Produits/Services),
          puis le bouton d'ajout pour les gestionnaires. Le chip actualise la
          page comme sur la version web. */}
      <div className="flex items-center gap-2 mb-4">
        <button
          type="button"
          onClick={() => window.location.reload()}
          title="Actualiser la page"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border bg-white border-gray-900 text-gray-900 shadow-sm whitespace-nowrap min-w-0 shrink"
        >
          <IcPackageOpen size={13} className="shrink-0" />
          <span className="truncate">{appText('products_title', 'Gestion des produits')}</span>
        </button>
        <div className="ml-auto inline-flex items-center gap-2 shrink-0">
          <div className="segp">
            <Link to="/products/promos" className="segp-off border border-transparent">
              <IcPercent size={13} /> Promo
            </Link>
          </div>
          {canManage && (
            <button
              onClick={() => navigate('/products/add')}
              aria-label="Ajouter un produit"
              className="btn-p h-9 w-9 rounded-xl shrink-0"
            >
              <IcPlus className="text-sm" />
            </button>
          )}
        </div>
      </div>
      {/* Owner 2026-09-09 : rangée recherche + Filtres identique à « Nouvelle vente »
          (/seller-sales) — champ `.inp h-12`, bouton Filtres `.btn-s h-12 w-[8.5rem]`,
          mb-3 ; la liste tombe à la même hauteur que le panier là-bas. */}
      <div className="flex gap-3 mb-3">
        <div className="relative flex-1 min-w-0">
          <IcSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher par nom ou code..."
            className="inp h-12 w-full pl-11 pr-10"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <IcClose />
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={() => setShowFilters((v) => !v)}
          aria-pressed={showFilters}
          className="btn-s relative h-12 w-[8.5rem] px-4 py-2.5 rounded-xl shrink-0"
        >
          <IcFilters className="text-sm" /> Filtres
        </button>
      </div>

      {/* Panneau de filtres — mêmes selects que la page Produits web. */}
      {showFilters && (
        <div className="bg-white border border-gray-200 rounded-2xl mb-4 p-4 grid grid-cols-1 gap-3">
          <select
            className="px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-700 focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900"
            value={selectedLaboratoire}
            onChange={(e) => setSelectedLaboratoire(e.target.value)}
          >
            <option value="">Laboratoire</option>
            {laboratoires.map((lab) => (
              <option key={lab.id} value={lab.id.toString()}>{lab.name}</option>
            ))}
          </select>
          <select
            className="px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-700 focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900"
            value={selectedCategory}
            onChange={(e) => { setSelectedCategory(e.target.value); setSelectedSubcategory(''); }}
          >
            <option value="">Catégorie</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id.toString()}>{category.name}</option>
            ))}
          </select>
          <select
            className="px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-700 focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 disabled:bg-gray-100 disabled:cursor-not-allowed"
            value={selectedSubcategory}
            onChange={(e) => setSelectedSubcategory(e.target.value)}
            disabled={!selectedCategory}
          >
            <option value="">Sous-catégorie</option>
            {filteredSubcategories.map((subcategory) => (
              <option key={subcategory.id} value={subcategory.id.toString()}>{subcategory.name}</option>
            ))}
          </select>
          <select
            aria-label="Disponibilité"
            className="px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-700 focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900"
            value={selectedStockState}
            onChange={(e) => setSelectedStockState(e.target.value)}
          >
            <option value="">Disponibilité</option>
            {STOCK_STATES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
      )}

      {loading && products.length === 0 ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        </div>
      ) : products.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center">
          <IcPackageOpen className="mx-auto text-5xl text-gray-300 mb-4" />
          <p className="text-gray-500 font-medium">Aucun produit trouvé</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {products.map((p) => (
            <ProductCard key={p.id} product={p} onOpen={setDetailProduct} />
          ))}
        </div>
      )}

      {!loading && products.length > 0 && products.length < count && (
        <button
          onClick={loadMore}
          className="btn-s mt-4 w-full py-2.5 rounded-xl font-medium transition-colors"
        >
          Charger plus ({products.length}/{count})
        </button>
      )}

      {detailProduct && (
        <ProductDetailModal
          product={detailProduct}
          onClose={() => setDetailProduct(null)}
          onEdit={handleEdit}
        />
      )}
    </div>
  );
}
